/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillFeedbackRequest,
  ListSkillFeedbackSecurity,
} from "../models/operations/listskillfeedback.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillFeedbackInfiniteQuery,
  buildSkillFeedbackQuery,
  prefetchSkillFeedback,
  prefetchSkillFeedbackInfinite,
  queryKeySkillFeedback,
  queryKeySkillFeedbackInfinite,
  SkillFeedbackInfiniteQueryData,
  SkillFeedbackPageParams,
  SkillFeedbackQueryData,
} from "./skillFeedback.core.js";
export {
  buildSkillFeedbackInfiniteQuery,
  buildSkillFeedbackQuery,
  prefetchSkillFeedback,
  prefetchSkillFeedbackInfinite,
  queryKeySkillFeedback,
  queryKeySkillFeedbackInfinite,
  type SkillFeedbackInfiniteQueryData,
  type SkillFeedbackPageParams,
  type SkillFeedbackQueryData,
};

export type SkillFeedbackQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listFeedback skills
 *
 * @remarks
 * List outcome counts, collection metrics, volume, and recent resolved feedback for a skill. Name-only feedback is excluded.
 */
export function useSkillFeedback(
  request: ListSkillFeedbackRequest,
  security?: ListSkillFeedbackSecurity | undefined,
  options?: QueryHookOptions<SkillFeedbackQueryData, SkillFeedbackQueryError>,
): UseQueryResult<SkillFeedbackQueryData, SkillFeedbackQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillFeedbackQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listFeedback skills
 *
 * @remarks
 * List outcome counts, collection metrics, volume, and recent resolved feedback for a skill. Name-only feedback is excluded.
 */
export function useSkillFeedbackSuspense(
  request: ListSkillFeedbackRequest,
  security?: ListSkillFeedbackSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillFeedbackQueryData,
    SkillFeedbackQueryError
  >,
): UseSuspenseQueryResult<SkillFeedbackQueryData, SkillFeedbackQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillFeedbackQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listFeedback skills
 *
 * @remarks
 * List outcome counts, collection metrics, volume, and recent resolved feedback for a skill. Name-only feedback is excluded.
 */
export function useSkillFeedbackInfinite(
  request: ListSkillFeedbackRequest,
  security?: ListSkillFeedbackSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillFeedbackInfiniteQueryData,
    SkillFeedbackQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SkillFeedbackInfiniteQueryData, SkillFeedbackPageParams>,
  SkillFeedbackQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillFeedbackInfiniteQueryData,
    SkillFeedbackQueryError,
    InfiniteData<SkillFeedbackInfiniteQueryData, SkillFeedbackPageParams>,
    QueryKey,
    SkillFeedbackPageParams
  >({
    ...buildSkillFeedbackInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listFeedback skills
 *
 * @remarks
 * List outcome counts, collection metrics, volume, and recent resolved feedback for a skill. Name-only feedback is excluded.
 */
export function useSkillFeedbackInfiniteSuspense(
  request: ListSkillFeedbackRequest,
  security?: ListSkillFeedbackSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillFeedbackInfiniteQueryData,
    SkillFeedbackQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SkillFeedbackInfiniteQueryData, SkillFeedbackPageParams>,
  SkillFeedbackQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillFeedbackInfiniteQueryData,
    SkillFeedbackQueryError,
    InfiniteData<SkillFeedbackInfiniteQueryData, SkillFeedbackPageParams>,
    QueryKey,
    SkillFeedbackPageParams
  >({
    ...buildSkillFeedbackInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillFeedbackData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillFeedbackQueryData,
): SkillFeedbackQueryData | undefined {
  const key = queryKeySkillFeedback(...queryKeyBase);

  return client.setQueryData<SkillFeedbackQueryData>(key, data);
}

export function invalidateSkillFeedback(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listFeedback", ...queryKeyBase],
  });
}

export function invalidateAllSkillFeedback(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listFeedback"],
  });
}
